import { Guid } from '../../../types/guid.type';
import { EntityBase, IEntityBaseInfo } from '../../entity/entitybase';
import { Identity, IIdentityInfo } from '../../identity/identity';
import { Team, ITeamInfo } from '../../organization/team/team';
import { Agency, IAgencyInfo } from '../../organization/agency/agency';
import type { EquipmentVehicle, IEquipmentVehicleInfo } from './equipment-vehicle';
import { instantiateEquipmentVehicle } from './equipment-lazy-instantiation';
import { LvfAssignmentType } from '../../../enum/supplychain/lvf-assignment-type';
import { LvfAssignmentPeriodType } from '../../../enum/supplychain/lvf-assignment-period-type';
import { MultiStatusConfig } from '../../multi-status/multi-status-config';
import { IMultiStatusConfigInfo } from '../../multi-status/multi-status-config';
import { MultiStatus } from '../../multi-status/multi-status';
import { IMultiStatusInfo } from '../../multi-status/multi-status';
import { IEquipmentAssignmentTicketInfo, EquipmentAssignmentTicket } from './equipment-assignment-ticket';
import { IPersonInfo, PersonInfo } from '../../core/person-info';

export interface IEquipmentAssignmentInfo extends IEntityBaseInfo {
  EquipmentUid?: Guid;
  VehicleUid?: Guid;
  AssignmentType?: LvfAssignmentType;
  DriverUid?: Guid;
  TeamUid?: Guid;
  PeriodType?: LvfAssignmentPeriodType;
  Reason?: string;
  Notes?: string;
  /** Restitution location (agency) — required before save. */
  LocationUid?: Guid;
  AuthorUid?: Guid;
  Author?: IPersonInfo;
  Driver?: IIdentityInfo;
  Team?: ITeamInfo;
  Location?: IAgencyInfo;
  Equipment?: IEquipmentVehicleInfo;
  EquipmentLabel?: string;
  TeamLabel?: string;
  DriverLabel?: string;
  WasReturned?: boolean;
  WasEquipmentReturned?: boolean;
  MileageAssignment?: number;
  MileageAtReturn?: number;
  RestitutionDate?: Date;
  RestitutionDateString?: string | null;
  TicketUids?: Guid[];
  EquipmentAssignmentTickets?: IEquipmentAssignmentTicketInfo[];
  VehicleAssignmentTickets?: IEquipmentAssignmentTicketInfo[];
}

export class EquipmentAssignment extends EntityBase {
  EquipmentUid?: Guid;
  VehicleUid?: Guid;
  AssignmentType?: LvfAssignmentType;
  DriverUid?: Guid;
  TeamUid?: Guid;
  PeriodType?: LvfAssignmentPeriodType;
  Reason?: string;
  Notes?: string;
  /** Restitution location (agency) — required before save. */
  LocationUid?: Guid;
  AuthorUid?: Guid;
  Author?: PersonInfo;
  Driver?: Identity;
  Team?: Team;
  Location?: Agency;
  Equipment?: EquipmentVehicle;
  MileageAssignment?: number;
  MileageAtReturn?: number;
  RestitutionDate?: Date;
  RestitutionDateString?: string | null;
  RestitutionDateDisplayed?: string | null;

  // Client side properties
  AssignmentTypeLabel?: string;
  PeriodTypeLabel?: string;
  EquipmentLabel?: string;
  TeamLabel?: string;
  DriverLabel?: string;
  WasReturned?: boolean;
  WasReturnedConfig?: MultiStatusConfig;
  TicketUids?: Guid[];
  EquipmentAssignmentTickets?: EquipmentAssignmentTicket[];
  VehicleAssignmentTickets?: EquipmentAssignmentTicket[];

  constructor(info: IEquipmentAssignmentInfo) {
    super(info);
    this.EquipmentUid = info.EquipmentUid ?? info.VehicleUid;
    this.VehicleUid = this.EquipmentUid;
    this.AssignmentType = info.AssignmentType;
    this.DriverUid = info.DriverUid;
    this.TeamUid = info.TeamUid;
    this.PeriodType = info.PeriodType;
    this.Reason = info.Reason;
    this.Notes = info.Notes;
    this.LocationUid = info.LocationUid;
    this.AuthorUid = info.AuthorUid;
    this.MileageAssignment = info.MileageAssignment;
    this.MileageAtReturn = info.MileageAtReturn;
    this.RestitutionDate = info.RestitutionDate;
    this.RestitutionDateString = info.RestitutionDateString;
    this.Author = PersonInfo.createInstance(PersonInfo, info.Author);
    this.Driver = info.Driver ? Identity.createInstance(Identity, info.Driver) : undefined;
    this.Team = info.Team ? Team.createInstance(Team, info.Team) : undefined;
    this.Location = info.Location ? Agency.createInstance(Agency, info.Location) : undefined;
    this.Equipment = info.Equipment ? instantiateEquipmentVehicle(info.Equipment) : undefined;

    // Client side properties
    this.EquipmentLabel = info.EquipmentLabel;
    this.TeamLabel = info.TeamLabel;
    this.DriverLabel = info.DriverLabel;
    this.WasReturned = EquipmentAssignment.resolveWasReturned(info);
    this.TicketUids = info.TicketUids ?? [];
    this.EquipmentAssignmentTickets = (info.EquipmentAssignmentTickets ?? info.VehicleAssignmentTickets ?? []).map(t => new EquipmentAssignmentTicket(t));
    this.VehicleAssignmentTickets = this.EquipmentAssignmentTickets;

    this.WasReturnedConfig = this.buildWasReturnedConfig();
    this.changeWasReturnedStyle();

    // Client side calculated properties
    this.AssignmentTypeLabel = this.AssignmentType !== undefined ? `app.lvfassignmenttype.${LvfAssignmentType[this.AssignmentType].toLowerCase()}` : '';
    this.PeriodTypeLabel = this.PeriodType !== undefined ? `app.lvfassignmentperiodtype.${LvfAssignmentPeriodType[this.PeriodType].toLowerCase()}` : '';
  }

  override title(): string {
    return `Assignation équipement : ${this.EquipmentLabel || this.EquipmentUid || 'Non défini'}`;
  }

  override description(): string {
    return `Type : ${this.AssignmentTypeLabel || 'Non défini'} - Période : ${this.PeriodTypeLabel || 'Non défini'}`;
  }

  override getDatePropertyNames(): string[] {
    return [...super.getDatePropertyNames(), 'RestitutionDate'];
  }

  static resolveWasReturned(info: Partial<IEquipmentAssignmentInfo>): boolean {
    if (info.WasReturned === true || info.WasEquipmentReturned === true) {
      return true;
    }

    return !!(info.RestitutionDateString?.trim() || info.RestitutionDate);
  }

  buildWasReturnedConfig(): MultiStatusConfig {
    return new MultiStatusConfig(<IMultiStatusConfigInfo>{
      Statuses: [
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.equipment.assignment.assigned',
          Style: 'color: var(--dropdown-bg); background-color: var(--purple-success)',
          Handler: () => {
            this.WasReturned = false;
            this.changeWasReturnedStyle();
          },
        }),
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.equipment.assignment.returned',
          Style: 'color: var(--module-title); background-color: var(--dropdown-bg)',
          Handler: () => {
            this.WasReturned = true;
            this.changeWasReturnedStyle();
          },
        }),
      ],
    });
  }

  changeWasReturnedStyle(): void {
    if (!this.WasReturnedConfig) return;

    if (!this.WasReturned) {
      this.WasReturnedConfig.Statuses[0].Style = 'color: var(--dropdown-bg); background-color: var(--purple-success)';
      this.WasReturnedConfig.Statuses[1].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
    } else {
      this.WasReturnedConfig.Statuses[0].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
      this.WasReturnedConfig.Statuses[1].Style = 'color: var(--dropdown-bg); background-color: #C6C8D3';
    }
  }
}
